/**
 * A receivables source: whatever owns the financial facts about a company's
 * customers — balances, direction, ageing, due dates — and nothing else.
 *
 * Google Sheets was the first source and remains one. TallyPrime (through the
 * Timely Connector) is the second. Both deliver the same thing: a snapshot of
 * normalised rows (`ImportedRow`) that the one planner (`planSync`) merges into
 * the book. Today, Customers and Reports never know which source a figure came
 * from, and a source never touches what the product owns (follow-ups, notes,
 * promises, collector, rank, contacts, cheques, history).
 */
import type { ImportedRow } from './sheetImport.js';
import type { AgeingBands } from './customer.js';

export type ReceivablesSourceKind = 'google_sheets' | 'tally';

/**
 * What the settings screen says about a source.
 *
 *   connected        — heard from recently, and the last sync succeeded recently
 *   connecting       — being set up (paired, company being chosen or confirmed),
 *                      or approved and waiting for its first sync
 *   offline          — not heard from within the offline window; the figures
 *                      shown are the last good snapshot, never "live"
 *   stale            — heard from, but no successful sync within the stale
 *                      window; the figures are the last good snapshot
 *   needs_attention  — heard from, but the last sync failed, the company check
 *                      failed, or the approving person lost access
 *   disconnected     — the connector was disconnected (revoked); the book keeps
 *                      its last figures and all of its history
 *   not_configured   — nothing connected
 */
export type SourceState = 'connected' | 'connecting' | 'offline' | 'stale' | 'needs_attention' | 'disconnected' | 'not_configured';

/** How old the figures are, stated honestly: never "live" when the source is quiet. */
export type Freshness = 'fresh' | 'stale' | 'unknown';

export interface SourceCompany {
    /** The source's own stable identity for the company (Tally: the company GUID). */
    id: string;
    name: string;
}

export interface ReceivablesSourceStatus {
    kind: ReceivablesSourceKind;
    state: SourceState;
    freshness: Freshness;
    sourceCompany: SourceCompany | null;
    /** The connector's device name, for a Tally source. */
    deviceName: string | null;
    lastSeenAt: string | null;
    lastSuccessAt: string | null;
    lastError: string | null;
}

/** What every source hands the planner: the same rows, whatever produced them. */
export interface ReceivablesSnapshot {
    source: { kind: ReceivablesSourceKind; company: SourceCompany };
    /** The date the balances and ageing are stated at (YYYY-MM-DD). */
    asOf: string;
    capturedAt: string;
    rows: ImportedRow[];
    summary: SnapshotSummary;
    /** Things the owner should know that are not errors (e.g. amounts that could not be aged). */
    diagnostics: string[];
}

/** The verification screen's figures, before anything is written. */
export interface SnapshotSummary {
    accounts: number;
    receivable: number;
    creditAccounts: number;
    creditAmount: number;
    /** Netted per bucket, Dr positive. */
    ageing: AgeingBands;
    /** Money in the totals that could not be put in an ageing bucket (Tally: on-account, or bill-wise off). */
    unaged: number;
}

/**
 * After this long without a word from a healthy connector, the source is
 * offline: four missed check-ins (it checks in every 5 minutes,
 * apps/connector `run`).
 */
export const OFFLINE_AFTER_MINUTES = 20;
/**
 * After this long without a word from a connector whose last report carried a
 * problem (TallyPrime closed, the company not open, a failed read), the source
 * is offline. Such a connector backs off before checking in again — up to 30
 * minutes (apps/connector `run`) — so silence shorter than this is the
 * back-off, not a stopped connector.
 */
export const OFFLINE_AFTER_FAILING_MINUTES = 35;
/** After this long without a successful sync, the figures are stale even if the connector is talking. */
export const STALE_AFTER_MINUTES = 6 * 60;

/**
 * The state and freshness a source is in, from what the server knows.
 * A connector that has gone quiet is offline, full stop: the last snapshot is
 * shown with its time, never presented as current.
 */
export function sourceState(input: {
    configured: boolean;
    status: 'pending' | 'active' | 'revoked' | null;
    lastSeenAt: string | null;
    lastSuccessAt: string | null;
    lastError: string | null;
    /** The connector's last report carried a problem, so it is backing off between check-ins. */
    reportedProblem?: boolean;
    now: Date;
}): { state: SourceState; freshness: Freshness } {
    if (!input.configured || input.status === null) return { state: 'not_configured', freshness: 'unknown' };
    const minutesSince = (iso: string | null) => (iso ? (input.now.getTime() - new Date(iso).getTime()) / 60_000 : Infinity);
    const seen = minutesSince(input.lastSeenAt);
    const success = minutesSince(input.lastSuccessAt);
    const freshness: Freshness = input.lastSuccessAt === null ? 'unknown' : success <= STALE_AFTER_MINUTES ? 'fresh' : 'stale';
    if (input.status === 'revoked') return { state: 'disconnected', freshness };
    if (input.status === 'pending') return { state: 'connecting', freshness };
    if (seen > (input.reportedProblem ? OFFLINE_AFTER_FAILING_MINUTES : OFFLINE_AFTER_MINUTES)) return { state: 'offline', freshness };
    if (input.lastError) return { state: 'needs_attention', freshness };
    if (input.lastSuccessAt === null) return { state: 'connecting', freshness };
    if (freshness === 'stale') return { state: 'stale', freshness };
    return { state: 'connected', freshness };
}
